import type {
  ReactNode} from "react";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import type { AlertColor } from "@mui/material/Alert";
import MuiAlert from "@mui/material/Alert";
import { setShowSnackbarRef } from "./snackbarBridge";

interface SnackbarOptions {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

interface SnackbarContextType {
  showSnackbar: (options: SnackbarOptions) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [duration, setDuration] = useState<number | undefined>(undefined);

  const showSnackbar = useCallback(
    ({ message, severity = "info", duration }: SnackbarOptions) => {
      setMessage(message);
      setSeverity(severity);
      setDuration(duration);
      setOpen(true);
    },
    []
  );

  useEffect(() => {
    setShowSnackbarRef(showSnackbar);
    // Можно очистить при размонтировании
    return () => setShowSnackbarRef(() => {});
  }, [showSnackbar]);

  const handleClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration ?? 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleClose}
          severity={severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {message}
        </MuiAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbarContext = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx)
    throw new Error(
      "useSnackbarContext must be used within a SnackbarProvider"
    );
  return ctx;
};
